// /batch — list user's batch jobs and create new ones.
// A "batch" is a list of scripts directed + rendered in one queue.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiError,
  batches,
  TTS_PROVIDERS,
  type BatchSummary,
  type DirectorChoice,
  type TtsProvider,
} from '../api.js';

const DIRECTOR_OPTIONS: { id: DirectorChoice; label: string }[] = [
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'gemini', label: 'Gemini 2.5 Pro' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Claude Sonnet' },
];

const STATUS_STYLES: Record<BatchSummary['status'], string> = {
  pending: 'text-muted bg-soft/60',
  processing: 'text-ink bg-soft/60',
  completed: 'text-accent bg-accent/10',
  failed: 'text-accent bg-accent/10',
  cancelled: 'text-muted bg-soft/60',
};

function fmtRel(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function BatchPage() {
  const [list, setList] = useState<BatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const r = await batches.list();
      setList(r.batches);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
      setList([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Batches</h1>
          <p className="text-muted text-sm">Direct and render many scripts at once. Useful for podcast series or audiobook chapters.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-ink text-parchment px-4 py-2 rounded-md hover:bg-ink/90 transition-colors text-sm"
        >
          New batch
        </button>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {createOpen && (
        <CreateBatchForm
          onClose={() => setCreateOpen(false)}
          onDone={() => {
            setCreateOpen(false);
            void refresh();
          }}
        />
      )}

      {list === null ? (
        <p className="text-muted text-sm">loading…</p>
      ) : list.length === 0 ? (
        <div className="border border-soft rounded-lg p-12 text-center">
          <p className="text-muted mb-2">No batches yet.</p>
          <p className="text-xs text-muted/70">Create one to direct multiple scripts at once.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((b) => (
            <li key={b.id}>
              <Link
                to={`/batch/${b.id}`}
                className="block border border-soft rounded-lg px-5 py-4 bg-white hover:border-muted/40 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="font-mono text-sm truncate">{b.id.slice(0, 8)}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_STYLES[b.status]}`}>
                      {b.status}
                    </span>
                  </div>
                  <span className="text-xs text-muted whitespace-nowrap">{fmtRel(b.updated_at)}</span>
                </div>
                <div className="text-xs text-muted/80 mt-1.5 flex items-center gap-3 flex-wrap">
                  <span>{b.completed_count}/{b.item_count} items</span>
                  {b.failed_count > 0 && <span className="text-accent">{b.failed_count} failed</span>}
                  <span>director: {b.director_model}</span>
                  {b.tts_provider && <span>TTS: {b.tts_provider}</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CreateBatchForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [items, setItems] = useState<{ title: string; script: string }[]>([
    { title: '', script: '' },
  ]);
  const [director, setDirector] = useState<DirectorChoice>('deepseek');
  const [ttsProvider, setTtsProvider] = useState<TtsProvider | null>(null);
  const [voiceId, setVoiceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function updateItem(i: number, patch: Partial<(typeof items)[number]>) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((prev) => [...prev, { title: '', script: '' }]);
  }
  function removeItem(i: number) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSubmit() {
    setError(null);
    const valid = items.filter((it) => it.title.trim() && it.script.trim().length >= 20);
    if (valid.length === 0) {
      setError('Add at least one item with a title and at least 20 characters of script.');
      return;
    }
    setSubmitting(true);
    try {
      await batches.create({
        items: valid,
        director_model: director,
        tts_provider: ttsProvider,
        tts_voice_id: voiceId.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Create failed.');
      setSubmitting(false);
    }
  }

  return (
    <div className="border border-soft rounded-lg bg-white px-5 py-5 mb-4">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-xl">New batch</h2>
        <button onClick={onClose} className="text-xs text-muted hover:text-ink">Cancel</button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted block mb-1">Director model</label>
            <select
              value={director}
              onChange={(e) => setDirector(e.target.value as DirectorChoice)}
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded text-sm"
            >
              {DIRECTOR_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">TTS provider (optional)</label>
            <select
              value={ttsProvider ?? ''}
              onChange={(e) => setTtsProvider(e.target.value === '' ? null : (e.target.value as TtsProvider))}
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded text-sm"
            >
              <option value="">—  direct only (no render)</option>
              {TTS_PROVIDERS.filter((p) => p.id !== 'playht').map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">Voice ID (if TTS)</label>
            <input
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder='e.g. "narrator" or "clone:..."'
              className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded text-sm font-mono"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-muted">Items ({items.length}, max 50)</label>
            <button
              onClick={addItem}
              disabled={items.length >= 50}
              className="text-xs text-muted hover:text-ink disabled:opacity-40"
            >
              + add another
            </button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border border-soft rounded p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    value={it.title}
                    onChange={(e) => updateItem(i, { title: e.target.value })}
                    placeholder={`Item ${i + 1} title`}
                    className="flex-1 px-3 py-1.5 bg-parchment/60 border border-soft rounded text-sm"
                  />
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(i)}
                      className="text-xs text-muted hover:text-accent px-2"
                    >
                      remove
                    </button>
                  )}
                </div>
                <textarea
                  value={it.script}
                  onChange={(e) => updateItem(i, { script: e.target.value })}
                  placeholder="Script (min 20 chars, max 50,000)"
                  rows={4}
                  className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded text-sm font-mono"
                />
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="text-xs text-accent bg-accent/5 border border-accent/20 rounded px-3 py-2">
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-accent text-parchment px-4 py-2 rounded text-sm hover:bg-accent/90 disabled:opacity-40 transition-colors"
        >
          {submitting ? 'Creating…' : 'Create batch'}
        </button>
      </div>
    </div>
  );
}
